/**
 * BookingLayout — Sidebar secundaria de Booking + outlet.
 * Cada sección es una URL: /booking/hoy, /booking/proximas, etc.
 * Muestra contadores en vivo + KPIs del día + FAB de "Nueva reserva".
 *
 * Mismo patrón que CatalogoLayout/PasadiasLayout.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  Box, Typography, List, ListItemButton, ListItemIcon, ListItemText, Chip,
  Fab, Tooltip, Tabs, Tab, Badge,
} from '@mui/material';
import TodayIcon from '@mui/icons-material/Today';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaidIcon from '@mui/icons-material/Paid';
import HistoryIcon from '@mui/icons-material/History';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import AddIcon from '@mui/icons-material/Add';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import BuildIcon from '@mui/icons-material/Build';
import { useReservaciones, categorizar } from './shared/useReservaciones';
// KpiBar movido a HoyPage — solo aplica al día actual.
import BusquedaGlobal from './shared/BusquedaGlobal';
import NuevaReservaWizard from './NuevaReservaWizard';
import ReservaSlideOver from './ReservaSlideOver';
import { API_BASE, getEmpresaId } from './shared/format';

const SECTIONS = [
  { path: 'hoy',        label: 'Hoy',         icon: <TodayIcon />,           color: '#0d9488' },
  { path: 'proximas',   label: 'Próximas',    icon: <EventAvailableIcon />,  color: '#0ea5e9' },
  { path: 'calendario', label: 'Calendario',  icon: <CalendarMonthIcon />,   color: '#a855f7' },
  { path: 'operacion',  label: 'Operación día', icon: <DirectionsBoatIcon />, color: '#0891b2' },
  { path: 'por-cobrar', label: 'Por cobrar',  icon: <PaidIcon />,            color: '#f59e0b' },
  { path: 'hold',       label: 'Hold',        icon: <HourglassBottomIcon />, color: '#8b5cf6' },
  { path: 'historico',  label: 'Histórico',   icon: <HistoryIcon />,         color: '#64748b' },
  // Correcciones sobre una reserva: se escribe su código y se elige qué
  // arreglar. Vive acá y no en Finanzas porque lo que se corrige es la
  // reserva; el efecto en la plata es consecuencia, no el punto de partida.
  { path: 'correcciones', label: 'Correcciones', icon: <BuildIcon />,        color: '#b45309',
    allowedRoles: ['admin', 'superadmin', 'coordinador', 'administrativo'] },
];

function rolActual() {
  try { return (JSON.parse(localStorage.getItem('user') || '{}').role || '').toLowerCase(); }
  catch { return ''; }
}

export default function BookingLayout() {
  const _rol = rolActual();
  const SECCIONES_VISIBLES = SECTIONS.filter(
    s => !s.allowedRoles || s.allowedRoles.includes(_rol));

  const navigate = useNavigate();
  const location = useLocation();
  const { reservaciones, refetch, isLoading } = useReservaciones();
  // KPIs globales eliminados — cada vista calcula los suyos localmente.
  const [wizardOpen, setWizardOpen] = useState(false);
  const [globalDetalleId, setGlobalDetalleId] = useState(null);

  // Cutoff de reservas del día — se lee una vez de la configuración de la
  // empresa y se pasa a HoyPage/ProximasPage vía outlet context. Se usa para
  // habilitar/deshabilitar el botón de borrar en las tablas.
  const [horaCutoff, setHoraCutoff] = useState('09:00');
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/turismo/configuracion/${getEmpresaId()}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data?.hora_cutoff_reservas_dia) {
          setHoraCutoff(String(data.hora_cutoff_reservas_dia).slice(0, 5));
        }
      })
      .catch(() => { /* usa el default 09:00 */ });
    return () => { cancelled = true; };
  }, []);

  const counts = useMemo(() => {
    const c = categorizar(reservaciones);
    return {
      hoy: c.hoy.length,
      proximas: c.proximas.length,
      'por-cobrar': c.porCobrar.length,
      hold: c.hold.length,
      historico: c.historico.length,
    };
  }, [reservaciones]);

  // Redirigir a "hoy" si entran a /booking sin sub-ruta
  React.useEffect(() => {
    if (location.pathname === '/booking' || location.pathname === '/booking/') {
      navigate('/booking/hoy', { replace: true });
    }
  }, [location.pathname, navigate]);

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', bgcolor: '#f8fafc' }}>
      {/* Sidebar secundario */}
      <Box sx={{
        width: 240, flexShrink: 0, bgcolor: '#fff',
        borderRight: '1px solid #e2e8f0',
        display: { xs: 'none', md: 'flex' }, flexDirection: 'column',
      }}>
        <Box sx={{ p: 2.5, pb: 1.5 }}>
          <Typography sx={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 700 }}>
            Booking
          </Typography>
        </Box>
        <List sx={{ px: 1, py: 0 }} dense>
          {SECCIONES_VISIBLES.map(s => {
            const isActive = location.pathname.includes(`/booking/${s.path}`);
            const c = counts[s.path];
            return (
              <ListItemButton key={s.path} onClick={() => navigate(`/booking/${s.path}`)}
                sx={{
                  borderRadius: 2, mb: 0.5, py: 0.75, pl: 1.5,
                  bgcolor: isActive ? `${s.color}15` : 'transparent',
                  borderLeft: isActive ? `3px solid ${s.color}` : '3px solid transparent',
                  '&:hover': { bgcolor: isActive ? `${s.color}25` : '#f1f5f9' },
                }}>
                <ListItemIcon sx={{ minWidth: 32, color: isActive ? s.color : '#94a3b8' }}>
                  {s.icon}
                </ListItemIcon>
                <ListItemText primary={s.label}
                  primaryTypographyProps={{
                    fontSize: 13, fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#0f172a' : '#475569',
                  }} />
                {c > 0 && (
                  <Chip size="small" label={c}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700,
                          bgcolor: isActive ? s.color : '#e2e8f0',
                          color: isActive ? '#fff' : '#64748b' }} />
                )}
              </ListItemButton>
            );
          })}
        </List>

        <Box sx={{ flex: 1 }} />

        <Box sx={{ p: 2, borderTop: '1px solid #f1f5f9' }}>
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', lineHeight: 1.5 }}>
            Las reservas se cargan desde todas las salidas. Hoy muestra las del día actual;
            Por cobrar las que tienen saldo {'>'} 0.
          </Typography>
        </Box>
      </Box>

      {/* Contenido — minWidth: 0 evita que un hijo ancho (tablas) fuerce al
          flexbox a expandirse más allá del viewport. Sin esto, en 1366×768
          la tabla de Próximas empuja al outlet y aparece scroll horizontal
          a nivel del viewport entero. */}
      <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', p: { xs: 2, md: 3 } }}>
        {/* Tabs móviles — sólo visibles en xs/sm. Reemplazan el sidebar oculto. */}
        <Box sx={{
          display: { xs: 'block', md: 'none' },
          mb: 2,
          mx: -2,                // bleed-edge para que el scroll se vea hasta los bordes
          px: 2,
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fff',
          position: 'sticky', top: -16, zIndex: 5,
        }}>
          <Tabs
            value={SECCIONES_VISIBLES.findIndex(s => location.pathname.includes(`/booking/${s.path}`)) === -1 ? 0
                  : SECCIONES_VISIBLES.findIndex(s => location.pathname.includes(`/booking/${s.path}`))}
            onChange={(_, idx) => navigate(`/booking/${SECCIONES_VISIBLES[idx].path}`)}
            variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
            sx={{
              minHeight: 44,
              '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: 12, px: 1.5 },
              '& .MuiTabs-indicator': { backgroundColor: '#0d9488', height: 3, borderRadius: 2 },
            }}
          >
            {SECCIONES_VISIBLES.map(s => {
              const c = counts[s.path] || 0;
              return (
                <Tab key={s.path}
                  icon={s.icon} iconPosition="start"
                  label={
                    <Badge badgeContent={c} color="primary" max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          bgcolor: s.color, color: '#fff',
                          fontSize: 9, height: 16, minWidth: 16, right: -6, top: 2,
                        },
                      }}>
                      <span style={{ paddingRight: c > 0 ? 10 : 0 }}>{s.label}</span>
                    </Badge>
                  }
                />
              );
            })}
          </Tabs>
        </Box>

        {/* KpiBar movido a HoyPage — sus métricas son específicas del día
            (Neto Entretur, Comisiones x pagar, CxC, Por cobrar muelle,
            Cortesías) y no aplican a Próximas/Histórico/Hold. */}

        {/* Búsqueda global cross-section — se OCULTA en /historico porque
            esa pantalla tiene su propio buscador server-side integrado en el
            panel de filtros (más potente: busca en toda la BD, no solo en el
            subset cargado). Evita redundancia y ruido para el operador. */}
        {!location.pathname.includes('/historico') && (
          <BusquedaGlobal reservaciones={reservaciones} onOpen={setGlobalDetalleId} />
        )}

        <Outlet context={{
          reservaciones, isLoading, refetch,
          onNueva: () => setWizardOpen(true),
          horaCutoff,
        }} />
      </Box>

      {/* SlideOver desde búsqueda global */}
      {globalDetalleId && (
        <ReservaSlideOver
          reservaId={globalDetalleId}
          onClose={() => setGlobalDetalleId(null)}
          onUpdated={refetch}
        />
      )}

      {/* FAB Nueva reserva — posicionado arriba del FAB del NaraChat (que
          vive en bottom: 20 right: 20 zIndex: 1300) para que no quede oculto. */}
      <Tooltip title="Nueva reserva" placement="left">
        <Fab onClick={() => setWizardOpen(true)}
          sx={{
            position: 'fixed', bottom: 96, right: 24, zIndex: 1301,
            background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
            color: '#fff', boxShadow: '0 8px 24px rgba(13, 148, 136, 0.4)',
            '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
          }}>
          <AddIcon />
        </Fab>
      </Tooltip>

      {wizardOpen && (
        <NuevaReservaWizard
          open={wizardOpen}
          onClose={() => setWizardOpen(false)}
          onCreated={() => { setWizardOpen(false); refetch(); }}
        />
      )}
    </Box>
  );
}
